import { Modal, message, Select, Checkbox, Button } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { putRequest, uploadFile } from "../../service";
import { getChangedFields } from "../../utils/getChangedFields";
import { UploadOutlined, PaperClipOutlined, DeleteOutlined } from "@ant-design/icons";

const AttendanceRuleEdit = (props) => {
  const { open, onRequestClose, employee } = props;
  const [loading, setLoading] = useState({ front: false, back: false });
  const [form, setForm] = useState({
    rule_applied: false,
    missing_attendance: false,
    late_arrival: false,
    hours_in_day: false,
    leave_ristrictions: false,
    overtime_policy: false,
    early_out: false,
    attendance_web: false,
    attendance_machine: false,
    frontSideImg: null,
    backSideImg: null
  });
  const originalFormRef = useRef({});

  useEffect(() => {
    if (employee) {
      const initialData = {
        rule_applied: employee.rule_applied || false,
        missing_attendance: employee.missing_attendance || false,
        late_arrival: employee.late_arrival || false,
        hours_in_day: employee.hours_in_day || false,
        leave_ristrictions: employee.leave_ristrictions || false,
        overtime_policy: employee.overtime_policy || false,
        early_out: employee.early_out || false,
        attendance_web: employee.attendance_web || false,
        attendance_machine: employee.attendance_machine || false,
        frontSideImg: employee.frontSideImg || null,
        backSideImg: employee.backSideImg || null
      };
      setForm(initialData);
      originalFormRef.current = initialData;
    }
  }, [employee]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setForm(prev => {
      const next = {
        ...prev,
        [name]: checked
      };
      // Enforce mutual exclusivity for attendance options
      if (checked) {
        if (name === 'attendance_web') {
          next.attendance_machine = false;
        } else if (name === 'attendance_machine') {
          next.attendance_web = false;
        }
      }
      return next;
    });
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      message.error('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('File size should be less than 5MB');
      return;
    }

    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const result = await uploadFile(file);
      if (result.message === 'File uploaded successfully to Cloudinary') {
        const fieldName = type === 'front' ? 'frontSideImg' : 'backSideImg';
        setForm(prev => ({
          ...prev,
          [fieldName]: result.url
        }));
        message.success('File uploaded successfully');
      } else {
        message.error('File upload failed');
      }
    } catch (error) {
      message.error('Failed to upload file');
      console.error('Upload error:', error);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleFileDelete = (type) => {
    const fieldName = type === 'front' ? 'frontSideImg' : 'backSideImg';
    setForm(prev => ({
      ...prev,
      [fieldName]: null
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = getChangedFields(originalFormRef.current, form);
      if (Object.keys(payload).length === 0) {
        message.info("No changes to update");
        return;
      }
      const response = await putRequest(`/api/employees?employeeId=${employee._id}`, payload);
      if (response.message === 'Employee updated successfully (some related data could not be loaded)') {
        message.success('Employee updated successfully')
        onRequestClose();
      }
      else {
        message.error(response.message || "Employee not updated")
      }
    } catch (error) {
      console.error('Error updating employee:', error);
    }
  };

  return (
    <Modal
      title="Time Rules and Attendance"
      open={open}
      onCancel={onRequestClose}
      footer={null}
      width={800}
    >
      <div className="flex items-start justify-between bg-white h-[65vh] overflow-auto">
        <div className="bg-form-bg w-full rounded-md px-4 py-2">
          <div className="mb-3">
            <h2 className="font-semibold mb-2">Time Rules:</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2">
                <Checkbox
                  name="rule_applied"
                  checked={form.rule_applied}
                  onChange={handleCheckboxChange}
                />
                <span>Attendance Rule Applied</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="missing_attendance"
                  checked={form.missing_attendance}
                  onChange={handleCheckboxChange}
                />
                <span>Missing Attendance</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="late_arrival"
                  checked={form.late_arrival}
                  onChange={handleCheckboxChange}
                />
                <span>Late Arrival</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="hours_in_day"
                  checked={form.hours_in_day}
                  onChange={handleCheckboxChange}
                />
                <span>Hours in a day</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="leave_ristrictions"
                  checked={form.leave_ristrictions}
                  onChange={handleCheckboxChange}
                />
                <span>Leave Restrictions</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="overtime_policy"
                  checked={form.overtime_policy}
                  onChange={handleCheckboxChange}
                />
                <span>Over Time Policy</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="early_out"
                  checked={form.early_out}
                  onChange={handleCheckboxChange}
                />
                <span>Early Out</span>
              </label>
            </div>
          </div>

          <div className="mb-3">
            <h2 className="font-semibold mb-2">Attendance Access:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-2">
                <Checkbox
                  name="attendance_web"
                  checked={form.attendance_web}
                  onChange={handleCheckboxChange}
                />
                <span>Enable CheckIn CheckOut Access on Web</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  name="attendance_machine"
                  checked={form.attendance_machine}
                  onChange={handleCheckboxChange}
                />
                <span>Enable CheckIn CheckOut Access on Machine</span>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <h2 className="font-semibold mb-2">CNIC/Driving License Documents:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full">
                <label className="block mb-2">Front Side</label>
                <div className="border border-dashed border-gray-500 rounded-md flex justify-between items-center py-2">
                  {form.frontSideImg === null && (
                    <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600">
                      Please choose a file
                    </div>
                  )}
                  {form.frontSideImg && (
                    <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600 flex items-center">
                      <PaperClipOutlined className="mr-1" />
                      {form.frontSideImg.length > 20 ? `${form.frontSideImg.substring(0, 20)}...` : form.frontSideImg}
                      <DeleteOutlined
                        className="ml-2 text-red-500 cursor-pointer"
                        onClick={() => handleFileDelete('front')}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    hidden
                    id="frontUpload"
                    accept=".jpg, .png, .jpeg"
                    onChange={(e) => handleFileUpload(e, 'front')}
                    disabled={loading.front}
                  />
                  <button
                    className="flex-1 text-md text-gray-600"
                    onClick={() => document.getElementById('frontUpload').click()}
                    disabled={loading.front}
                  >
                    <UploadOutlined /> {loading.front ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
              <div className="w-full">
                <label className="block mb-2">Back Side</label>
                <div className="border border-dashed border-gray-500 rounded-md flex justify-between items-center py-2">
                  {form.backSideImg === null && (
                    <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600">
                      Please choose a file
                    </div>
                  )}
                  {form.backSideImg && (
                    <div className="flex-1 bg-form-bg ml-2 text-md text-gray-600 flex items-center">
                      <PaperClipOutlined className="mr-1" />
                      {form.backSideImg.length > 20 ? `${form.backSideImg.substring(0, 20)}...` : form.backSideImg}
                      <DeleteOutlined
                        className="ml-2 text-red-500 cursor-pointer"
                        onClick={() => handleFileDelete('back')}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    hidden
                    id="backUpload"
                    accept=".jpg, .png, .jpeg"
                    onChange={(e) => handleFileUpload(e, 'back')}
                    disabled={loading.back}
                  />
                  <button
                    className="flex-1 text-md text-gray-600"
                    onClick={() => document.getElementById('backUpload').click()}
                    disabled={loading.back}
                  >
                    <UploadOutlined /> {loading.back ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
         
          <div className="flex items-center justify-end gap-3 my-2">
            <Button onClick={onRequestClose} >Cancel</Button>
            <Button
              onClick={handleSubmit}
              type="primary"
              className="bg-primary text-white"
            >
              Update Address
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AttendanceRuleEdit;
